import { eigenSysteem, gedragenSystemen, isNummerVerwijzing, passendeSystemen } from "./complements";
import { getArticles } from "./catalog";

/**
 * Welke systeemcodes zijn ÉCHTE systemen?
 *
 * `eigenSysteem()` leest de kop van het leveranciersnummer, en dat levert voor de meeste artikelen
 * iets betekenisloos op (`27U302030`, `21BELS344`). Zouden we dat overal tonen, dan stond er bij
 * duizenden producten een "Systeem"-badge met een code die niemand iets zegt — en zou de zoeker
 * ermee volstromen.
 *
 * Een code telt daarom als systeem wanneer:
 *  - een ánder artikel er expliciet naar verwijst ("t.b.v. 70KLIK", "t.b.v. TL-6020"), of
 *  - minstens TWEE artikelen hem in hun eigen naam dragen — TL-6021 op 48 afdekkappen en eindkappen
 *    is een systeem, "V-780" op één losse kruk is een typenummer, of
 *  - hij handmatig in Productbeheer is gezet.
 *
 * Artikelnummer-verwijzingen ("tbv 661501720") koppelen wel, maar zijn nooit een systeem om te
 * tonen: een badge "Systeem #661501720" zegt een klant niets.
 */
let cache: { versie: number; set: Set<string> } | null = null;

export function bekendeSystemen(systeemPerSlug?: Record<string, string>): Set<string> {
  const artikelen = getArticles();
  // Grof versienummer: verandert de catalogus of het aantal handmatige systemen, dan opnieuw.
  const versie = artikelen.length * 1000 + Object.keys(systeemPerSlug ?? {}).length;
  if (cache?.versie === versie) return cache.set;
  const set = new Set<string>();
  const gedragen = new Map<string, number>();
  for (const a of artikelen) {
    for (const c of passendeSystemen(a.name)) if (!isNummerVerwijzing(c)) set.add(c);
    for (const c of gedragenSystemen(a.name)) gedragen.set(c, (gedragen.get(c) ?? 0) + 1);
  }
  for (const [c, n] of gedragen) if (n >= 2) set.add(c);
  for (const v of Object.values(systeemPerSlug ?? {})) {
    const c = v.trim().toUpperCase();
    if (c) set.add(c);
  }
  cache = { versie, set };
  return set;
}

/**
 * Het systeem dat je bij dit artikel TOONT — of null als er geen herkenbaar systeem is.
 * Handmatig wint; anders een code in de eigen naam, het leveranciersnummer of een verwijzing,
 * maar alleen als die code een bekend systeem is.
 */
export function toonbaarSysteem(
  a: { slug?: string; name: string; articleNumber?: string },
  ov?: { systeemPerSlug?: Record<string, string> } | null,
): string | null {
  const hand = a.slug ? ov?.systeemPerSlug?.[a.slug]?.trim().toUpperCase() : "";
  if (hand) return hand;
  const bekend = bekendeSystemen(ov?.systeemPerSlug);
  const gedragen = gedragenSystemen(a.name).find((c) => bekend.has(c));
  if (gedragen) return gedragen;
  const eigen = eigenSysteem(a.articleNumber);
  if (eigen && bekend.has(eigen)) return eigen;
  return passendeSystemen(a.name).find((c) => !isNummerVerwijzing(c) && bekend.has(c)) ?? null;
}
